import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, ShieldCheck, ShieldX, AlertTriangle, RefreshCcw, Mail, Lock, Unlock, Copy, ExternalLink, Inbox,
} from 'lucide-react';
import { toast } from 'sonner';

const StatusPill = ({ ok, label }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
      ok ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
         : 'bg-rose-100    text-rose-800    border-rose-300'
    }`}
    data-testid={`pill-${label.toLowerCase().replace(/\W+/g, '-')}-${ok ? 'pass' : 'fail'}`}
  >
    {ok ? <ShieldCheck className="w-3 h-3" /> : <ShieldX className="w-3 h-3" />} {label}: {ok ? 'PASS' : 'FAIL'}
  </span>
);

const CopyBox = ({ text, testid }) => (
  <div className="flex items-center gap-2">
    <code className="flex-1 text-xs bg-gray-900 text-emerald-300 px-3 py-2 rounded font-mono break-all" data-testid={testid}>
      {text}
    </code>
    <Button
      size="sm" variant="outline"
      onClick={() => { navigator.clipboard.writeText(text); toast.success('Copied'); }}
      data-testid={`${testid}-copy`}
    >
      <Copy className="w-3.5 h-3.5" />
    </Button>
  </div>
);

const EmailHealth = () => {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/system/email-health');
      setHealth(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load email health');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (paused, reason = '') => {
    setBusy(true);
    try {
      await api.post('/admin/system/email-pause', { paused, reason });
      toast.success(paused ? 'Outbound email paused' : 'Outbound email resumed');
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && detail.blockers) {
        toast.error(`Blocked: ${detail.blockers.join(' · ')}`, { duration: 8000 });
      } else {
        toast.error(detail || 'Failed to update email pause');
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading || !health) {
    return <div className="p-6 text-sm text-gray-500">Loading email health…</div>;
  }

  const { dns, kill_switch, sender_email, frontend_url, preview_link_warning, ready_to_resume } = health;

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="admin-email-health">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Mail className="w-6 h-6 text-indigo-600" /> Email Deliverability Health
        </h1>
        <Button variant="outline" size="sm" className="ml-auto" onClick={load} data-testid="refresh-btn">
          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/system/email-log')} data-testid="view-email-log-btn">
          <Inbox className="w-3.5 h-3.5 mr-1" /> View Email Log
        </Button>
      </div>

      {/* Effective state banner */}
      <Card
        className={`mb-6 border-2 ${kill_switch.effective_paused ? 'border-rose-300 bg-rose-50' : 'border-emerald-300 bg-emerald-50'}`}
        data-testid="effective-state-banner"
      >
        <CardContent className="p-4 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {kill_switch.effective_paused
                ? <Lock className="w-5 h-5 text-rose-600" />
                : <Unlock className="w-5 h-5 text-emerald-600" />}
              <span className="font-bold text-lg">
                {kill_switch.effective_paused ? 'OUTBOUND EMAIL: PAUSED' : 'OUTBOUND EMAIL: LIVE'}
              </span>
            </div>
            {kill_switch.db_reason && (
              <div className="text-xs text-gray-600 ml-7">Reason: {kill_switch.db_reason}</div>
            )}
            {kill_switch.db_set_by && (
              <div className="text-xs text-gray-500 ml-7">
                Set by {kill_switch.db_set_by} · {new Date(kill_switch.db_set_at).toLocaleString()}
              </div>
            )}
          </div>
          {kill_switch.effective_paused ? (
            <Button
              onClick={() => toggle(false, 'Admin resumed via dashboard')}
              disabled={busy || !ready_to_resume || kill_switch.env_paused}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="resume-btn"
              title={
                kill_switch.env_paused ? 'Env flag EMAIL_DELIVERY_PAUSED=true must be cleared first'
                  : !ready_to_resume ? 'DNS or FRONTEND_URL not clean — see blockers below'
                  : 'Resume outbound email'
              }
            >
              <Unlock className="w-4 h-4 mr-1" /> Resume Sending
            </Button>
          ) : (
            <Button
              onClick={() => toggle(true, 'Admin paused via dashboard')}
              disabled={busy}
              variant="outline"
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
              data-testid="pause-btn"
            >
              <Lock className="w-4 h-4 mr-1" /> Pause Sending
            </Button>
          )}
        </CardContent>
      </Card>

      {/* DNS health card */}
      <Card className="mb-6">
        <CardHeader><CardTitle className="text-lg">DNS Authentication</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <StatusPill ok={dns.spf.ok}   label="SPF" />
            <StatusPill ok={dns.dkim.ok}  label="DKIM" />
            <StatusPill ok={dns.dmarc.ok} label="DMARC" />
            <StatusPill ok={!preview_link_warning} label="Production URL" />
          </div>

          {/* SPF */}
          <div data-testid="dns-spf-block">
            <div className="font-semibold text-sm text-gray-800 mb-1">SPF (root TXT on ievntur.com)</div>
            <div className="text-xs text-gray-500 mb-1">Current:</div>
            <CopyBox text={dns.spf.current || '(missing)'} testid="dns-spf-current" />
            {!dns.spf.ok && (
              <>
                <div className="text-xs text-gray-500 mt-2 mb-1">Required:</div>
                <CopyBox text={dns.spf.recommended} testid="dns-spf-recommended" />
                <div className="text-xs text-rose-600 mt-1">⚠ <code>include:sendgrid.net</code> is missing — this is what tells Gmail to trust mail signed by SendGrid on your behalf.</div>
              </>
            )}
          </div>

          {/* DKIM */}
          <div data-testid="dns-dkim-block">
            <div className="font-semibold text-sm text-gray-800 mb-1">DKIM (SendGrid CNAMEs)</div>
            <div className="text-xs space-y-1">
              <div><span className="font-mono text-gray-600">s1._domainkey</span> → <span className="font-mono">{dns.dkim.s1 || '(missing)'}</span></div>
              <div><span className="font-mono text-gray-600">s2._domainkey</span> → <span className="font-mono">{dns.dkim.s2 || '(missing)'}</span></div>
            </div>
          </div>

          {/* DMARC */}
          <div data-testid="dns-dmarc-block">
            <div className="font-semibold text-sm text-gray-800 mb-1">DMARC (_dmarc.ievntur.com TXT)</div>
            <div className="text-xs text-gray-500 mb-1">Current:</div>
            <CopyBox text={dns.dmarc.current || '(missing)'} testid="dns-dmarc-current" />
            {!dns.dmarc.ok && (
              <>
                <div className="text-xs text-gray-500 mt-2 mb-1">Required (monitoring mode while stabilizing):</div>
                <CopyBox text={dns.dmarc.recommended} testid="dns-dmarc-recommended" />
                <div className="text-xs text-rose-600 mt-1">⚠ Current policy is <code>p={dns.dmarc.policy}</code>. While SPF is failing, this actively pushes mail to spam. Use <code>p=none</code> for ~14 days, then ratchet to quarantine.</div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sender + frontend config */}
      <Card className="mb-6">
        <CardHeader><CardTitle className="text-lg">Application Configuration</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center gap-3">
            <span className="text-gray-600 w-32">From email:</span>
            <code className="font-mono text-emerald-700 font-semibold">{sender_email}</code>
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">aligned</Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-gray-600 w-32">Frontend URL:</span>
            <code className="font-mono text-xs break-all">{frontend_url}</code>
            {preview_link_warning && (
              <Badge className="bg-rose-100 text-rose-800 border-rose-300 shrink-0">
                <AlertTriangle className="w-3 h-3 mr-1" /> PREVIEW LEAK
              </Badge>
            )}
          </div>
          {preview_link_warning && (
            <div className="text-xs text-rose-600 ml-32">
              Email links currently embed <code>preview.emergentagent.com</code> — major spam trigger. Update <code>FRONTEND_URL</code> in backend/.env to your production domain (e.g. <code>https://app.ievntur.com</code>).
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-gray-500 text-center">
        Domain: <code>{dns.domain}</code> · Audit re-runs from{' '}
        <a href="https://mxtoolbox.com/SuperTool.aspx?action=mx%3aievntur.com&run=toolpage"
           target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1">
          MXToolbox <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
};

export default EmailHealth;
